import { useAuthStore } from "@/composables/store/admin/useAuthStore";
import { useContractorAuthStore } from "@/composables/store/contractor/useAuthStore";
import { useUserAuthStore } from "@/composables/store/user/useAuthStore";
import { useMasterAuthStore } from "@/composables/store/master/useAuthStore";
import { LOCALSTORAGE } from "@/utils/constants";

export const setupGuards = (router) => {
  router.beforeEach(async (to, from, next) => {
    const isUserRoute = to.fullPath.startsWith("/user/");
    const isContractorRoute = to.fullPath.startsWith("/contractor/");
    const isMasterRoute = to.fullPath.startsWith("/master/");

    // Determine which auth store to use based on route
    let authStore;
    if (isUserRoute) {
      authStore = useUserAuthStore();
    } else if (isContractorRoute) {
      authStore = useContractorAuthStore();
    } else if (isMasterRoute) {
      authStore = useMasterAuthStore();
    } else {
      authStore = useAuthStore();
    }

    // Initialize auth state if not already done
    if (!authStore.isInitialized) {
      await authStore.initializeAuth();
    }

    const isLoggedIn = authStore.isAuthenticated;

    // List of routes that don't require authentication
    const publicRoutes = [
      // Admin
      "admin-authentication-login",
      "admin-authentication-register",
      "admin-authentication-forgot-password",
      "admin-authentication-reset-password",

      // User
      "user-authentication-login",
      "user-authentication-register",
      "user-authentication-forgot-password",
      "user-authentication-reset-password",

      // Contractor
      "contractor-authentication-login",
      "contractor-authentication-register",
      "contractor-authentication-forgot-password",
      "contractor-authentication-reset-password",

      // Master
      "master-authentication-login",
      "master-authentication-forgot-password",
      "master-authentication-reset-password",

      // Guest + landing
      "guest-guest-complaint",
      "guest-complaint-token",
      "landing",
    ];

    /**
     * IMPORTANT FIX:
     * These pages should NOT auto-redirect even if user is logged in.
     * This matches common template behavior (admin doesn't bounce away).
     */
    const allowEvenIfLoggedIn = [
      "admin-authentication-forgot-password",
      "admin-authentication-reset-password",

      "user-authentication-forgot-password",
      "user-authentication-reset-password",

      "contractor-authentication-forgot-password",
      "contractor-authentication-reset-password",

      "master-authentication-forgot-password",
      "master-authentication-reset-password",
    ];

    // Make ALL /guest/ pages public (no login required)
    if (to.path.startsWith("/guest/")) {
      next();
      return;
    }

    // Handle public routes (login, register, etc)
    if (publicRoutes.includes(to.name)) {
      // If this public route is allowed even when logged in, DO NOT redirect away.
      if (allowEvenIfLoggedIn.includes(to.name)) {
        next();
        return;
      }

      // If user is already logged in and tries to access login/register page, redirect to dashboard
      if (isLoggedIn && to.meta.unauthenticatedOnly) {
        if (isUserRoute) {
          next("/user/dashboard/home");
        } else if (isContractorRoute) {
          next("/contractor/dashboard/home");
        } else if (isMasterRoute) {
          next("/master/dashboard/home");
        } else {
          const companyType = localStorage.getItem(LOCALSTORAGE.COMPANY_TYPE);
          if (companyType === "agency") {
            next({ name: "admin-dashboards-analytics" });
          } else if (companyType === "client") {
            next({ name: "dashboards-client" });
          } else {
            next({ name: "admin-dashboards-analytics" });
          }
        }
        return;
      }

      next();
      return;
    }

    // For protected routes, check if user is logged in
    if (!isLoggedIn) {
      let loginPath;
      if (isUserRoute) {
        loginPath = "/user/authentication/login";
      } else if (isContractorRoute) {
        loginPath = "/contractor/authentication/login";
      } else if (isMasterRoute) {
        loginPath = "/master/authentication/login";
      } else {
        loginPath = "/admin/authentication/login";
      }

      next({
        path: loginPath,
        query: { to: to.fullPath },
      });
      return;
    }

    next();
  });
};
